import { useState, useEffect, useMemo } from 'react';

export const useIframeCommunication = (agentId, isOpen, widgetSettings) => {
  const [isMobile, setIsMobile] = useState(false);

  // Calcula dimensões dinâmicas baseado no tooltip
  const dimensions = useMemo(() => {
  if (!widgetSettings) {
    return { width: '110px', height: '100px' };
  }

  const tooltip = widgetSettings.tooltipMessage;
  const position = widgetSettings.tooltipPosition || 'left';
  
  if (!tooltip || tooltip.trim() === '') {
    return { width: '110px', height: '100px' };
  }

  const charCount = tooltip.length;
  const avgCharWidth = 8;
  const padding = 24;
  const arrow = 12;
  
  let tooltipWidth = 0;
  let tooltipHeight = 40;
  
  // Diferentes estratégias por posição
  if (position === 'top' || position === 'bottom') {
    // Para topo/baixo: largura menor, permite mais linhas
    tooltipWidth = Math.min(charCount * avgCharWidth + padding, 200); // Max 200px
    
    const maxCharsPerLine = Math.floor(tooltipWidth / avgCharWidth);
    const lines = Math.ceil(charCount / maxCharsPerLine);
    tooltipHeight = Math.min(lines * 20 + 16, 80); // Max 80px de altura
    
  } else {
    // Para esquerda/direita: largura maior, menos linhas
    tooltipWidth = Math.min(charCount * avgCharWidth + padding, 280); // Max 280px
    
    const maxCharsPerLine = Math.floor(tooltipWidth / avgCharWidth);
    const lines = Math.ceil(charCount / maxCharsPerLine);
    tooltipHeight = Math.min(lines * 20 + 16, 60); // Menos altura
  }

  let totalWidth = 110;
  let totalHeight = 100;

  switch (position) {
    case 'left':
      totalWidth = 110 + tooltipWidth + arrow + 16;
      totalHeight = Math.max(100, tooltipHeight + 20);
      break;
      
    case 'right':
      totalWidth = 110 + tooltipWidth + arrow + 16;
      totalHeight = Math.max(100, tooltipHeight + 20);
      break;
      
    case 'top':
      totalWidth = Math.max(140, tooltipWidth + 40); // Margem lateral maior
      totalHeight = 100 + tooltipHeight + arrow + 20; // Espaço extra
      break;
      
    case 'bottom':
      // Tooltip fica ABAIXO do botão - precisa de MAIS espaço
      totalWidth = Math.max(140, tooltipWidth + 40);
      totalHeight = 100 + arrow + tooltipHeight + 24; // botão + seta + tooltip + margem extra
      break;
  }

  return {
    width: `${totalWidth}px`,
    height: `${totalHeight}px`,
    tooltipWidth: `${tooltipWidth}px`,
    tooltipHeight: `${tooltipHeight}px`
  };
}, [widgetSettings]);

  // Handle parent window communication
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'parent-info') {
        setIsMobile(event.data.isMobile);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    window.parent.postMessage({ 
      type: 'agent-chat-ready',
      agentId 
    }, '*');
    
    return () => window.removeEventListener('message', handleMessage);
  }, [agentId]);
    
  // Send resize messages to parent
  useEffect(() => {
    const sendMessage = (type, data = {}) => {
      window.parent.postMessage({ 
        type: `agent-chat-${type}`,
        agentId,
        ...data 
      }, '*');
    };

    sendMessage('ready', { isOpen });
    
    if (isOpen) {
      sendMessage('opened');

      if (isMobile) {
        sendMessage('resize', { 
          width: '100vw',
          height: '100vh',
          bottom: '0',
          right: '0',
          borderRadius: '0',
          isOpen: true,
          isMobile: true
        });
      } else {
        sendMessage('resize', { 
          width: '405px',
          height: '590px',
          bottom: '20px',
          right: '20px',
          borderRadius: '20px',
          isOpen: true,
          isMobile: false
        });
      }
    } else {
      sendMessage('closed');
      sendMessage('resize', {
        width: dimensions.width,
        height: dimensions.height,
        bottom: '20px',
        right: '20px',
        borderRadius: '20px',
        isOpen: false,
        isMobile: false,
        // Envia também dimensões do tooltip para ajustes finos no cliente se necessário
        tooltipInfo: widgetSettings ? {
          hasTooltip: !!widgetSettings.tooltipMessage?.trim(),
          position: widgetSettings.tooltipPosition,
          width: dimensions.tooltipWidth,
          height: dimensions.tooltipHeight
        } : null
      });
    }
  }, [isOpen, agentId, isMobile, dimensions, widgetSettings]);

  // Set transparent background for iframe
  useEffect(() => {
    const s = document.createElement('style');
    s.id = 'quora-ai-chat';
    s.textContent = `
      html, body, #root, #root > div {
        background: transparent !important;
      }
    `;
    document.head.appendChild(s);
    return () => {
      const el = document.getElementById('quora-ai-chat');
      if (el) el.remove();
    };
  }, []);

  return { isMobile };
};